import apiClient from "./apiClient";
import ActiveUpload from "../models/ActiveUpload";
import CompletedUpload from "../models/CompletedUpload";

const INVALID_FILENAME_CHARS = /[^a-zA-Z0-9-_. ]/g;

export function sanitizeFilename(filename) {
  return (filename || "").replace(INVALID_FILENAME_CHARS, "");
}

export const DUPLICATE_FILENAME_WARNING = "A file with this name already exists";

export function normalizeFilenameKey(filename) {
  return sanitizeFilename(filename).toLowerCase();
}

async function fileExistsByName(filename, target_org_id) {
  const params = {
    filename,
    // FileList records exist from the start of upload; without this flag
    // the API hides anything still processing, so re-uploads slip through.
    include_processing: true,
    page_size: 1,
  };
  if (target_org_id) params.target_org_id = target_org_id;

  const { data } = await apiClient.get("/files", { params });
  return (data?.count ?? 0) > 0;
}

const UploadService = {
  async getActiveUploads({
    page_size = 25,
    page = 1,
    search = "",
    ordering = "",
    target_org_id,
    statuses = [
      "Waiting to process",
      "Processing",
      "Analysing Video",
      "Storing Results",
    ],
  } = {}) {
    const params = new URLSearchParams();
    params.append("page_size", page_size);
    params.append("page", page);

    statuses.forEach((status) => params.append("status", status));

    if (search) params.append("search", search);
    if (target_org_id) params.append("target_org_id", target_org_id);
    if (ordering) params.append("ordering", ordering);

    const { data } = await apiClient.get(
      `/files/processing?${params.toString()}`
    );

    return {
      ...data,
      results: data.results.map(ActiveUpload.fromServerModel),
    };
  },

  async getCompletedUploads({
    page_size = 25,
    page = 1,
    search = "",
    target_org_id,
    ordering = "-created_time",
  } = {}) {
    const params = {
      upload_completed: true,
      page_size,
      page,
      ordering,
    };
    if (search) params.search = search;
    if (target_org_id) params.target_org_id = target_org_id;

    const { data } = await apiClient.get("/files", { params });

    return {
      ...data,
      results: data.results.map(CompletedUpload.fromServerModel),
    };
  },

  async validateFilename({ file_name, target_org_id }) {
    const cleaned_name = sanitizeFilename(file_name);
    let valid = true;
    let warnings = [];
    let errors = [];

    if (cleaned_name !== file_name) {
      warnings.push("Invalid characters will be removed");
    }

    const MIN_LENGTH = 1;
    const MAX_LENGTH = 200;
    if (cleaned_name.length < MIN_LENGTH || cleaned_name.length > MAX_LENGTH) {
      valid = false;
      errors.push(
        `Filename must be between ${MIN_LENGTH} and ${MAX_LENGTH} characters`
      );
      return {
        file_name,
        cleaned_name,
        valid,
        warnings,
        errors,
      };
    }

    const namesToCheck =
      cleaned_name === file_name ? [cleaned_name] : [cleaned_name, file_name];
    const matches = await Promise.all(
      namesToCheck.map((filename) => fileExistsByName(filename, target_org_id))
    );

    if (matches.some(Boolean)) {
      warnings.push(DUPLICATE_FILENAME_WARNING);
    }

    return {
      file_name,
      cleaned_name,
      valid,
      warnings,
      errors,
    };
  },

  async deleteProcessingUpload(uploadId) {
    await apiClient.delete(`/files/processing/${uploadId}`);
  },

  async retryProcessingUpload(upload) {
    const { fileListId, targetOrganisationId } = upload;

    const { data } = await apiClient.post(
      `/files/processing/${fileListId}/retry`,
      null,
      {
        params: { target_org_id: targetOrganisationId },
      }
    );

    return data;
  },
};

export default UploadService;
